import { FEATURE_KEYS, MODULE_KEYS, OPERATION_IDS, ROUTE_IDS } from '../routes/ids.js';
import { getRouteById } from '../routes/effective-routes.js';
import { AUDIT_TABLE, USERS_TABLE as BASE_USERS_TABLE } from './table-registry.js';
const tk = (value) => value;
const telemetry = (value) => value;
const formId = (value) => value;
const fieldId = (value) => value;
const validationRule = (value) => value;
const commandId = (value) => value;
const queryId = (value) => value;
function routePath(routeId, rowField = 'id') {
    const route = getRouteById(routeId);
    if (!route)
        throw new Error(`Unknown platform administration route ${routeId}`);
    return route.path.replace(/:[A-Za-z0-9_-]+/, `:${rowField}`);
}
function descriptorMetadata(id, feature = FEATURE_KEYS.ADMIN_USERS) {
    return {
        id,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature,
        privacyClassification: 'INTERNAL',
        telemetryId: telemetry(`experience.platform-admin.${id}`),
        accessibilityLabelKey: tk(`a11y.${id}`),
        availability: 'AVAILABLE',
        owner: 'experience-registry',
    };
}
function column(id, field, overrides = {}) {
    return {
        ...descriptorMetadata(id),
        // Columns use a11y.col.${id} to avoid collisions with a11y.* namespace
        // parents (e.g. a11y.action is both a namespace and a column id).
        accessibilityLabelKey: tk(`a11y.col.${id}`),
        labelKey: tk(`col.${field}`),
        field,
        fieldType: 'text',
        sortable: true,
        filterable: true,
        allowedFilterOperators: ['equals', 'contains'],
        searchable: true,
        groupable: false,
        hideable: true,
        defaultVisible: true,
        requiredVisible: false,
        resizable: true,
        reorderable: true,
        pinnable: false,
        defaultWidth: 180,
        minWidth: 90,
        maxWidth: 500,
        align: 'left',
        nullDisplayBehavior: 'dash',
        ...overrides,
    };
}
function editAction() {
    return {
        ...descriptorMetadata('action.platformUser.edit'),
        operationId: OPERATION_IDS.PLATFORM_USERS_UPDATE,
        labelKey: tk('action.edit'),
        iconKey: 'edit',
        kind: 'navigate',
        intent: 'primary',
        idempotencyRequired: false,
        successBehavior: 'NAVIGATE_TO',
        targetRouteId: ROUTE_IDS.PLATFORM_USERS_EDIT,
        successNavigateTo: routePath(String(ROUTE_IDS.PLATFORM_USERS_EDIT)),
        errorBehavior: 'SHOW_INLINE_ERROR',
        order: 10,
        group: 'navigation',
    };
}
function disableAction() {
    return {
        ...descriptorMetadata('action.platformUser.disable'),
        operationId: OPERATION_IDS.PLATFORM_USERS_DISABLE,
        labelKey: tk('action.delete'),
        iconKey: 'delete',
        kind: 'command',
        method: 'DELETE',
        commandId: commandId('cmd.admin.platform-users.delete'),
        intent: 'danger',
        idempotencyRequired: true,
        confirmation: {
            titleKey: tk('user:confirmDeleteTitle'),
            messageKey: tk('user:confirmDelete'),
            confirmLabelKey: tk('action.delete'),
            cancelLabelKey: tk('action.cancel'),
        },
        successBehavior: 'REFRESH_TABLE',
        successMessageKey: tk('user:deleteSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common:error.unexpected'),
        order: 20,
        group: 'danger',
    };
}
export const PLATFORM_USERS_TABLE = {
    ...BASE_USERS_TABLE,
    id: 'table.admin.platformUsers',
    dataSourceId: queryId('query.admin.platform-users.list'),
    // The base users table gates its PII email column with an organization-scoped
    // permission; the platform surface requires the platform-scoped equivalent.
    columns: BASE_USERS_TABLE.columns
        .filter((item) => item.field !== 'branchId')
        .map((item) => item.field === 'email'
        ? { ...item, permissionRequirement: 'platform:users:email:view' }
        : item),
    rowActions: [editAction(), disableAction()],
    defaultSort: undefined,
    serverSideSorting: false,
    capabilities: {
        ...(BASE_USERS_TABLE.capabilities ?? {}),
        sorting: false,
        filtering: true,
        globalSearch: true,
        selection: false,
        create: true,
    },
    toolbar: {
        ...(BASE_USERS_TABLE.toolbar ?? {}),
        showSelectedCount: false,
        showBulkActions: false,
        showCreateAction: true,
    },
    // Platform users export is client-side only; no server-side export operation exists.
    privacyAndExportPolicy: {
        ...(BASE_USERS_TABLE.privacyAndExportPolicy ?? {}),
        auditExports: false,
    },
};
export const PLATFORM_MODULES_TABLE = {
    ...descriptorMetadata('table.admin.platformModules', FEATURE_KEYS.ADMIN_ROLES),
    titleKey: tk('page.moduleAssignments.title'),
    dataSourceId: queryId('query.admin.platform-modules.list'),
    rowIdField: 'id',
    columns: [
        column('platformModule.organization', 'organizationName', {
            requiredVisible: true,
            defaultWidth: 240,
        }),
        column('platformModule.organizationCode', 'organizationCode', { defaultWidth: 150 }),
        column('platformModule.module', 'module', { requiredVisible: true, defaultWidth: 190 }),
        column('platformModule.status', 'status', { fieldType: 'status', defaultWidth: 130 }),
        column('platformModule.maxBranches', 'maxBranches', {
            fieldType: 'number',
            searchable: false,
            defaultWidth: 120,
        }),
        column('platformModule.maxUsers', 'maxUsers', {
            fieldType: 'number',
            searchable: false,
            defaultWidth: 120,
        }),
        column('platformModule.effectiveFrom', 'effectiveFrom', {
            fieldType: 'datetime',
            searchable: false,
            defaultWidth: 190,
        }),
        column('platformModule.expectedUpdatedAt', 'expectedUpdatedAt', {
            fieldType: 'datetime',
            defaultVisible: false,
            searchable: false,
        }),
    ],
    pagination: { pageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: false,
    serverSideFiltering: false,
    capabilities: {
        sorting: false,
        filtering: true,
        globalSearch: true,
        grouping: false,
        aggregation: false,
        pagination: true,
        selection: false,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        columnPinning: false,
        savedViews: false,
        export: true,
        refresh: true,
        density: true,
        create: true,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: false,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: true,
    },
    rowActions: [
        {
            ...descriptorMetadata('action.platformModule.revoke', FEATURE_KEYS.ADMIN_ROLES),
            operationId: OPERATION_IDS.PLATFORM_MODULES_REVOKE,
            labelKey: tk('action.delete'),
            iconKey: 'delete',
            kind: 'command',
            method: 'DELETE',
            commandId: commandId('cmd.admin.platform-modules.delete'),
            intent: 'danger',
            idempotencyRequired: true,
            confirmation: {
                titleKey: tk('common:confirmAction'),
                messageKey: tk('common:confirmAction'),
                confirmLabelKey: tk('action.delete'),
                cancelLabelKey: tk('action.cancel'),
                fields: [
                    {
                        name: 'confirmationReason',
                        labelKey: tk('common.reason'),
                        fieldType: 'text',
                        required: true,
                    },
                ],
            },
            successBehavior: 'REFRESH_TABLE',
            successMessageKey: tk('common:success'),
            errorBehavior: 'SHOW_INLINE_ERROR',
            errorMessageKey: tk('common:error.unexpected'),
            order: 10,
            group: 'danger',
        },
    ],
};
export const PLATFORM_ACCESS_INSPECTOR_TABLE = {
    ...descriptorMetadata('table.admin.platformAccessInspector', FEATURE_KEYS.ADMIN_ROLES),
    titleKey: tk('page.platformAccessInspector.title'),
    dataSourceId: queryId('query.admin.platform-access-inspector.list'),
    rowIdField: 'id',
    columns: [
        column('platformAccess.persona', 'persona', { requiredVisible: true, defaultWidth: 180 }),
        column('platformAccess.roleCode', 'roleCode', { requiredVisible: true, defaultWidth: 230 }),
        column('platformAccess.permission', 'permission', { requiredVisible: true, defaultWidth: 320 }),
        column('platformAccess.module', 'module', { defaultWidth: 150 }),
        column('platformAccess.resource', 'resource', { defaultWidth: 200 }),
        column('platformAccess.action', 'action', { defaultWidth: 150 }),
        column('platformAccess.scope', 'resourceScope', { defaultWidth: 160 }),
    ],
    pagination: { pageSize: 50, pageSizeOptions: [25, 50, 100] },
    operationMode: 'server',
    serverSidePagination: true,
    serverSideSorting: false,
    serverSideFiltering: false,
    capabilities: {
        sorting: false,
        filtering: true,
        globalSearch: true,
        grouping: false,
        aggregation: false,
        pagination: true,
        selection: false,
        columnVisibility: true,
        columnResizing: true,
        columnReordering: true,
        columnPinning: false,
        savedViews: false,
        export: true,
        refresh: true,
        density: true,
        create: false,
    },
    toolbar: {
        showResultCount: true,
        showGlobalSearch: true,
        showFilterRowToggle: false,
        showColumnChooser: true,
        showDensity: true,
        showRefresh: true,
        showExport: true,
        showCreateAction: false,
    },
};
export const PLATFORM_AUDIT_TABLE = {
    ...AUDIT_TABLE,
    id: 'table.admin.platformAudit',
    dataSourceId: queryId('query.admin.platform-audit.list'),
    // The base audit table gates its PII actor-email column with an
    // organization-scoped permission; the platform surface requires the
    // platform-scoped equivalent.
    columns: AUDIT_TABLE.columns.map((item) => item.field === 'userEmail'
        ? { ...item, permissionRequirement: 'platform:audit:user-email:view' }
        : item),
    capabilities: {
        ...(AUDIT_TABLE.capabilities ?? {}),
        globalSearch: true,
    },
    export: {
        formats: ['csv'],
        defaultFormat: 'csv',
        scopes: ['all'],
        defaultScope: 'all',
        formulaInjectionProtection: true,
        serverExport: {
            operationId: OPERATION_IDS.PLATFORM_AUDIT_EXPORT,
            endpoint: '/api/platform/audit/export',
            method: 'POST',
            filterParameterMap: {
                entityType: 'entityType',
                action: 'action',
                organizationId: 'organizationId',
                branchId: 'branchId',
                createdAt: 'occurredFrom',
            },
            searchParameter: 'search',
        },
    },
    toolbar: {
        ...(AUDIT_TABLE.toolbar ?? {}),
        showGlobalSearch: true,
    },
};
function required() {
    return {
        rules: [{ ruleId: validationRule('required'), messageKey: tk('validation.required') }],
        validateWhen: 'blur',
    };
}
function field(id, name, overrides = {}) {
    return {
        id: fieldId(id),
        name,
        labelKey: tk(id),
        fieldType: 'text',
        value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
        layout: { md: 6 },
        ...overrides,
    };
}
function cancelAction(feature) {
    return {
        ...descriptorMetadata('action.cancel', feature),
        labelKey: tk('action.cancel'),
        iconKey: 'cancel',
        kind: 'localUi',
        submitBehavior: 'standalone',
        intent: 'secondary',
        idempotencyRequired: false,
        successBehavior: 'NO_OP',
        errorBehavior: 'SHOW_INLINE_ERROR',
        order: 10,
        group: 'secondary',
    };
}
function submitAction(id, operationId, command, feature) {
    return {
        ...descriptorMetadata(id, feature),
        // Submit actions use a11y.action.${id} to avoid collisions with the
        // parent form's accessibilityLabelKey (a11y.${formId}).
        accessibilityLabelKey: tk(id.startsWith('action.') ? `a11y.${id}` : `a11y.action.${id}`),
        operationId,
        labelKey: tk('action.save'),
        iconKey: 'save',
        kind: 'command',
        submitBehavior: 'form_submit',
        submitConfig: {
            validatesForm: true,
            requiresDirty: true,
            requiresValid: true,
            resetBehavior: 'toServerValues',
        },
        commandId: commandId(command),
        intent: 'primary',
        idempotencyRequired: true,
        successBehavior: 'NAVIGATE_BACK',
        successMessageKey: tk('common.saveSuccess'),
        errorBehavior: 'SHOW_INLINE_ERROR',
        errorMessageKey: tk('common.error.unexpected'),
        order: 20,
        group: 'primary',
    };
}
const platformRoleOptions = [
    ['PLATFORM_ROOT_PERSONA', 'Platform Root'],
    ['PLATFORM_ADMIN_PERSONA', 'Platform Admin'],
    ['PLATFORM_USER_PERSONA', 'Platform User'],
].map(([value, label]) => ({
    value,
    labelKey: tk(`platform.role.${label.replaceAll(' ', '').toLowerCase()}`),
}));
const statusOptions = ['ACTIVE', 'INACTIVE', 'SUSPENDED'].map((value) => ({
    value,
    labelKey: tk(`enum.userStatus.${value}`),
}));
function platformUserFields(mode) {
    const readonly = mode === 'view';
    const output = readonly
        ? {
            value: {
                hiddenValuePolicy: 'unregister',
                submitBehavior: 'exclude',
                outputOnly: true,
            },
        }
        : {};
    return [
        ...(mode !== 'edit'
            ? [
                field('field.platformUser.email', 'email', {
                    validation: mode === 'create' ? required() : undefined,
                    layout: { md: 12 },
                    ...output,
                }),
            ]
            : []),
        ...(mode === 'edit'
            ? [
                field('field.platformUser.expectedVersion', 'expectedVersion', {
                    fieldType: 'hidden',
                    value: {
                        hiddenValuePolicy: 'preserve',
                        submitBehavior: 'include',
                        retainWhenHidden: true,
                    },
                }),
            ]
            : []),
        field('field.platformUser.firstName', 'firstName', {
            validation: readonly ? undefined : required(),
            ...output,
        }),
        field('field.platformUser.lastName', 'lastName', {
            validation: readonly ? undefined : required(),
            ...output,
        }),
        ...(mode === 'create'
            ? [
                field('field.platformUser.password', 'password', {
                    fieldType: 'password',
                    validation: {
                        rules: [
                            { ruleId: validationRule('required'), messageKey: tk('validation.required') },
                            {
                                ruleId: validationRule('minLength'),
                                params: { min: 8 },
                                messageKey: tk('validation.minLength'),
                            },
                        ],
                        validateWhen: 'blur',
                    },
                    sensitive: { payloadBehavior: 'submit', displayBehavior: 'hide' },
                    layout: { md: 12 },
                    value: { hiddenValuePolicy: 'clear', clearWhenHidden: true, submitBehavior: 'include' },
                }),
            ]
            : []),
        field('field.platformUser.roleCode', 'roleCode', {
            fieldType: 'select',
            options: platformRoleOptions,
            validation: readonly ? undefined : required(),
            layout: { md: 6 },
            ...output,
        }),
        ...(mode !== 'create'
            ? [
                field('field.platformUser.status', 'status', {
                    fieldType: 'select',
                    options: statusOptions,
                    layout: { md: 6 },
                    ...output,
                }),
            ]
            : []),
    ];
}
function platformUserForm(id, mode, operationId, command) {
    const fields = platformUserFields(mode);
    return {
        ...descriptorMetadata(id),
        id: formId(id),
        mode,
        titleKey: tk(`form.platformUser.${mode}Title`),
        fields,
        sections: [
            {
                id: `${id}.section`,
                titleKey: tk(`form.platformUser.${mode}Title`),
                fields: fields.map((item) => item.id),
                layout: { columns: 2, gap: 16 },
            },
        ],
        actions: command
            ? [
                cancelAction(FEATURE_KEYS.ADMIN_USERS),
                submitAction(`${id}.submit`, operationId, command, FEATURE_KEYS.ADMIN_USERS),
            ]
            : [cancelAction(FEATURE_KEYS.ADMIN_USERS)],
        validation: command ? { validateWhen: 'submit' } : undefined,
        submission: command
            ? {
                defaultCommandId: commandId(command),
                includeFields: fields
                    .filter((item) => item.value?.submitBehavior !== 'exclude' && !item.value?.outputOnly)
                    .map((item) => item.id),
                successMessageKey: tk('form.user.success'),
                errorMessageKey: tk('form.user.error'),
            }
            : { excludeFields: fields.map((item) => item.id) },
        operationId,
    };
}
export const PLATFORM_USER_CREATE_FORM = platformUserForm('form.admin.platformUser.create', 'create', OPERATION_IDS.PLATFORM_USERS_CREATE, 'cmd.admin.platform-users.create');
export const PLATFORM_USER_EDIT_FORM = platformUserForm('form.admin.platformUser.edit', 'edit', OPERATION_IDS.PLATFORM_USERS_UPDATE, 'cmd.admin.platform-users.update');
export const PLATFORM_USER_VIEW_FORM = platformUserForm('form.admin.platformUser.view', 'view', OPERATION_IDS.PLATFORM_USERS_READ);
const moduleOptions = [
    'INVESTMENT',
    'ACCOUNTING',
    'TAX',
    'RECONCILIATION',
    'CORPORATE_ACTIONS',
    'REPORTING',
    'AUTH',
    'DASHBOARD',
    'SETTINGS',
    'ADMIN',
    'NAVIGATION',
    'DATA',
    'INSTRUMENTS',
    'IMPORT',
    'FACTS',
    'HOLDINGS',
    'REGULATORY',
].map((value) => ({ value, labelKey: tk(`enum.module.${value}`) }));
export const PLATFORM_MODULE_ASSIGNMENT_FORM = {
    ...descriptorMetadata('form.admin.platformModuleAssignment.create', FEATURE_KEYS.ADMIN_ROLES),
    id: formId('form.admin.platformModuleAssignment.create'),
    mode: 'create',
    titleKey: tk('form.platformModuleAssignment.createTitle'),
    fields: [
        field('field.platformModule.organizationId', 'organizationId', {
            fieldType: 'select',
            validation: required(),
            optionSource: {
                adapterId: 'api',
                operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_LIST,
                valuePath: 'id',
                labelPath: 'name',
                allowSearch: true,
                minimumSearchLength: 0,
            },
            layout: { md: 12 },
        }),
        field('field.platformModule.module', 'module', {
            fieldType: 'select',
            options: moduleOptions,
            validation: required(),
            layout: { md: 6 },
        }),
        field('field.platformModule.maxBranches', 'maxBranches', {
            fieldType: 'number',
            value: { defaultValue: 10, hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
            layout: { md: 6 },
        }),
        field('field.platformModule.maxUsers', 'maxUsers', {
            fieldType: 'number',
            value: { defaultValue: 100, hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
            layout: { md: 6 },
        }),
        field('field.platformModule.effectiveFrom', 'effectiveFrom', {
            fieldType: 'datetime',
            value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
            layout: { md: 6 },
        }),
        field('field.platformModule.effectiveTo', 'effectiveTo', {
            fieldType: 'datetime',
            value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
            layout: { md: 6 },
        }),
        field('field.platformModule.graceEndsAt', 'graceEndsAt', {
            fieldType: 'datetime',
            value: { hiddenValuePolicy: 'unregister', submitBehavior: 'include' },
            layout: { md: 6 },
        }),
    ],
    sections: [
        {
            id: 'form.admin.platformModuleAssignment.create.section',
            titleKey: tk('form.platformModuleAssignment.createTitle'),
            fields: [
                fieldId('field.platformModule.organizationId'),
                fieldId('field.platformModule.module'),
                fieldId('field.platformModule.maxBranches'),
                fieldId('field.platformModule.maxUsers'),
                fieldId('field.platformModule.effectiveFrom'),
                fieldId('field.platformModule.effectiveTo'),
                fieldId('field.platformModule.graceEndsAt'),
            ],
            layout: { columns: 2, gap: 16 },
        },
    ],
    actions: [
        cancelAction(FEATURE_KEYS.ADMIN_ROLES),
        submitAction('form.admin.platformModuleAssignment.create.submit', OPERATION_IDS.PLATFORM_MODULES_ASSIGN, 'cmd.admin.platform-modules.create', FEATURE_KEYS.ADMIN_ROLES),
    ],
    validation: { validateWhen: 'submit' },
    submission: {
        defaultCommandId: commandId('cmd.admin.platform-modules.create'),
        includeFields: [
            fieldId('field.platformModule.organizationId'),
            fieldId('field.platformModule.module'),
            fieldId('field.platformModule.maxBranches'),
            fieldId('field.platformModule.maxUsers'),
            fieldId('field.platformModule.effectiveFrom'),
            fieldId('field.platformModule.effectiveTo'),
            fieldId('field.platformModule.graceEndsAt'),
        ],
        successMessageKey: tk('common.saveSuccess'),
        errorMessageKey: tk('common.error.unexpected'),
    },
    operationId: OPERATION_IDS.PLATFORM_MODULES_ASSIGN,
};
export const PLATFORM_ADMIN_TABLE_REGISTRY = Object.freeze({
    [String(PLATFORM_USERS_TABLE.id)]: PLATFORM_USERS_TABLE,
    [String(PLATFORM_MODULES_TABLE.id)]: PLATFORM_MODULES_TABLE,
    [String(PLATFORM_ACCESS_INSPECTOR_TABLE.id)]: PLATFORM_ACCESS_INSPECTOR_TABLE,
    [String(PLATFORM_AUDIT_TABLE.id)]: PLATFORM_AUDIT_TABLE,
});
export const PLATFORM_ADMIN_FORM_REGISTRY = Object.freeze({
    [String(PLATFORM_USER_CREATE_FORM.id)]: PLATFORM_USER_CREATE_FORM,
    [String(PLATFORM_USER_EDIT_FORM.id)]: PLATFORM_USER_EDIT_FORM,
    [String(PLATFORM_USER_VIEW_FORM.id)]: PLATFORM_USER_VIEW_FORM,
    [String(PLATFORM_MODULE_ASSIGNMENT_FORM.id)]: PLATFORM_MODULE_ASSIGNMENT_FORM,
});
